import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container } from '@material-ui/core';
import { FormStatus, QRCodeForms, qrCodeQuery, qrCodeService, sessionQuery } from '@lib/state';
import { FormHub, useFormEvents, useObservable, LoadingIndicator, Feedback } from '@lib/common';
import { QrCodeType } from '@lib/state/api/generated/properties';
import { FeedbackForm, MaintentanceTicketForm } from '../components';
import { SubmitFeedbackFormFields } from '../models/submitFeedbackFormFields';

export const FeedbackPage: React.FC = () => {
  const queryParams = new URLSearchParams(useLocation().search);
  const id = queryParams.get('id');
  const propertyId = useObservable(sessionQuery.propertyId);
  const qrCode = useObservable(qrCodeQuery.activeQRCode);
  const maintenanceTicketNumber = useObservable(qrCodeQuery.maintenanceTicketNumber);

  const [{ status: SubmitFeedbackStatus, error: SubmitFeedbackError }] = useFormEvents(
    QRCodeForms.SubmitFeedback
  );
  const [{ status: LoadFeedbackStatus, error: LoadFeedbackError }] = useFormEvents(
    QRCodeForms.LoadQRCode
  );

  const submit = useCallback(
    ({ notes, photo }: SubmitFeedbackFormFields) => {
      if (qrCode) {
        switch (qrCode.type) {
          case QrCodeType.MaintenanceTicket: {
            qrCodeService.submitTicket(
              qrCode.id,
              qrCode.propertyId,
              {
                notes: notes,
              },
              photo[0]
            );
            break;
          }
          case QrCodeType.Feedback: {
            qrCodeService.submitFeedback(qrCode.id, qrCode.propertyId, {
              comments: notes,
            });
            break;
          }
        }
      }
    },
    [qrCode]
  );

  useEffect(() => {
    if (id && propertyId) {
      qrCodeService.getQRCodeByID(propertyId, id);
      qrCodeService.resetMaintenanceTicketNumber();
    }
  }, [id, propertyId]);

  if (!id || LoadFeedbackStatus === FormStatus.Error) {
    return (
      <Feedback show={true} severity="error">
        {LoadFeedbackError?.message ?? 'Invalid QR Code'}
      </Feedback>
    );
  }

  if (!propertyId || !qrCode) return <LoadingIndicator fillContainer />;

  return (
    <Container maxWidth="md">
      <FormHub onSubmit={submit}>
        {qrCode.type === QrCodeType.Feedback && (
          <FeedbackForm status={SubmitFeedbackStatus} error={SubmitFeedbackError} qrcode={qrCode} />
        )}
        {qrCode.type === QrCodeType.MaintenanceTicket && (
          <MaintentanceTicketForm
            status={SubmitFeedbackStatus}
            error={SubmitFeedbackError}
            qrcode={qrCode}
            maintenanceTicketNumber={maintenanceTicketNumber}
          />
        )}
      </FormHub>
    </Container>
  );
};
